import { MONUMENT_FIREPOWER_BONUS } from "./buildings";
import { isCannonTile } from "./cannonTile";
import { removeUnitWithCaptureReset } from "./combatResolve";
import { coordKey, getTile, inBounds, manhattan } from "./grid";
import { applyDamageToStacks, normalizedHpPercent } from "./stacks";
import type { Coord, GameEvent, GameState, PlayerId, Tile, Unit, UnitKind } from "./types";
import { MAX_HP, UNIT_DEFS, effectiveAttackHp, effectiveAttackKind } from "./units";

export const CANNON_DAMAGE = 50;
export const CANNON_MIN_RANGE = 3;
export const CANNON_MAX_RANGE = 4;

const CANNON_DAMAGE_BY_ATTACKER: Partial<Record<UnitKind, number>> = {
  rifleman: 5,
  rpgInfantry: 15,
  lightTank: 15,
  heavyTank: 45,
  antiAir: 5,
  artillery: 40,
  rockets: 55,
  battleCopter: 20,
  bomber: 65,
};

function cannonHp(tile: Pick<Tile, "kind" | "hp">): number {
  if (!isCannonTile(tile)) return 0;
  return tile.hp ?? MAX_HP;
}

function countCannons(tiles: readonly Tile[]): number {
  return tiles.filter((tile) => isCannonTile(tile)).length;
}

export function allCannonsDestroyed(tiles: readonly Tile[]): boolean {
  return countCannons(tiles) === 0;
}

export function canUnitDamageCannon(unit: Pick<Unit, "kind" | "cargo">): boolean {
  return (CANNON_DAMAGE_BY_ATTACKER[effectiveAttackKind(unit)] ?? 0) > 0;
}

export function damagePercentVsCannon(attackerKind: UnitKind): number {
  return CANNON_DAMAGE_BY_ATTACKER[attackerKind] ?? 0;
}

function isCannonInAttackRange(distance: number): boolean {
  return distance >= CANNON_MIN_RANGE && distance <= CANNON_MAX_RANGE;
}

export function computeCannonAttackTileKeys(state: GameState, pos: Coord): string[] {
  const keys: string[] = [];
  for (let dx = -CANNON_MAX_RANGE; dx <= CANNON_MAX_RANGE; dx++) {
    const yBound = CANNON_MAX_RANGE - Math.abs(dx);
    for (let dy = -yBound; dy <= yBound; dy++) {
      const dist = Math.abs(dx) + Math.abs(dy);
      if (!isCannonInAttackRange(dist)) continue;
      const tile: Coord = { x: pos.x + dx, y: pos.y + dy };
      if (!inBounds(state, tile)) continue;
      keys.push(coordKey(tile));
    }
  }
  return keys;
}

export function computeDamageToCannon({
  attacker,
  attackerMonumentCount = 0,
}: {
  attacker: Unit;
  attackerMonumentCount?: number;
}): number {
  const basePct = CANNON_DAMAGE_BY_ATTACKER[effectiveAttackKind(attacker)] ?? 0;
  if (basePct <= 0) return 0;
  const hpFactor = normalizedHpPercent(effectiveAttackHp(attacker)) / MAX_HP;
  const firepowerMult = 1 + MONUMENT_FIREPOWER_BONUS * Math.max(0, attackerMonumentCount);
  return Math.ceil(basePct * hpFactor * firepowerMult);
}

function setTileAt({ state, pos, tile }: { state: GameState; pos: Coord; tile: Tile }): GameState {
  const index = pos.y * state.width + pos.x;
  return {
    ...state,
    tiles: state.tiles.map((existing, i) => (i === index ? tile : existing)),
  };
}

function destroyCannonAt({ state, pos }: { state: GameState; pos: Coord }): GameState {
  return setTileAt({ state, pos, tile: { kind: "plain" } });
}

export function applyDamageToCannon({
  state,
  pos,
  damage,
}: {
  state: GameState;
  pos: Coord;
  damage: number;
}): { state: GameState; destroyed: boolean; remainingHp: number } {
  const tile = getTile(state, pos);
  if (!isCannonTile(tile)) {
    return { state, destroyed: false, remainingHp: 0 };
  }
  const remainingHp = Math.max(0, cannonHp(tile) - Math.max(0, damage));
  if (remainingHp <= 0) {
    return { state: destroyCannonAt({ state, pos }), destroyed: true, remainingHp: 0 };
  }
  return {
    state: setTileAt({
      state,
      pos,
      tile: { ...tile, hp: remainingHp },
    }),
    destroyed: false,
    remainingHp,
  };
}

function compareCannonTargets({ a, b, from }: { a: Unit; b: Unit; from: Coord }): number {
  const costDiff = UNIT_DEFS[b.kind].cost - UNIT_DEFS[a.kind].cost;
  if (costDiff !== 0) return costDiff;
  const distDiff = manhattan(from, a.pos) - manhattan(from, b.pos);
  if (distDiff !== 0) return distDiff;
  return a.id.localeCompare(b.id);
}

export function selectCannonTarget({
  state,
  cannonPos,
  enemyOf,
}: {
  state: GameState;
  cannonPos: Coord;
  enemyOf: PlayerId;
}): Unit | null {
  const candidates = state.units.filter((unit) => {
    if (unit.owner === enemyOf) return false;
    return isCannonInAttackRange(manhattan(cannonPos, unit.pos));
  });
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => compareCannonTargets({ a, b, from: cannonPos }));
  return candidates[0] ?? null;
}

function setUnitHp(state: GameState, { id, hp }: { id: string; hp: number }): GameState {
  return {
    ...state,
    units: state.units.map((unit) => (unit.id === id ? { ...unit, hp } : unit)),
  };
}

function fireCannonAtTarget({
  state,
  cannonPos,
  target,
  playerId,
}: {
  state: GameState;
  cannonPos: Coord;
  target: Unit;
  playerId: PlayerId;
}): { state: GameState; events: GameEvent[] } {
  const applied = applyDamageToStacks({ unit: target, damage: CANNON_DAMAGE });
  const events: GameEvent[] = [
    {
      type: "cannonFired",
      playerId,
      pos: cannonPos,
      targetId: target.id,
      targetKind: target.kind,
      damage: CANNON_DAMAGE,
      killed: applied.killed,
    },
  ];
  if (applied.killed) {
    return {
      state: removeUnitWithCaptureReset({ state, id: target.id, events }),
      events,
    };
  }
  return {
    state: setUnitHp(state, { id: target.id, hp: applied.hp }),
    events,
  };
}

export function fireOwnedCannons(state: GameState): { state: GameState; events: GameEvent[] } {
  const playerId = state.currentPlayer;
  const events: GameEvent[] = [];
  let next = state;

  const cannonPositions: Coord[] = [];
  for (let y = 0; y < next.height; y++) {
    for (let x = 0; x < next.width; x++) {
      const tile = getTile(next, { x, y });
      if (isCannonTile(tile) && tile.owner === playerId) {
        cannonPositions.push({ x, y });
      }
    }
  }

  for (const pos of cannonPositions) {
    const tile = getTile(next, pos);
    if (!isCannonTile(tile) || tile.owner !== playerId) continue;
    const target = selectCannonTarget({ state: next, cannonPos: pos, enemyOf: playerId });
    if (!target) continue;
    const result = fireCannonAtTarget({
      state: next,
      cannonPos: pos,
      target,
      playerId,
    });
    next = result.state;
    events.push(...result.events);
  }

  return { state: next, events };
}

export function findCannonPositions(state: GameState): Coord[] {
  const positions: Coord[] = [];
  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      if (isCannonTile(getTile(state, { x, y }))) {
        positions.push({ x, y });
      }
    }
  }
  return positions;
}

export function cannonAt(state: GameState, pos: Coord): Tile | undefined {
  const tile = getTile(state, pos);
  return isCannonTile(tile) ? tile : undefined;
}
